import { useState, useEffect, useRef, useCallback } from "react";
import { Lock } from "lucide-react";
import Slider from "../slider/slider.tsx";
import Slider2 from "../slider2/slider2.tsx";
import Slider3 from "../slider3/slider3.tsx";
import Tooltip from "../tooltip/tooltip.tsx";
import "./sliderConnector.css";

type SliderType = "1-2" | "2-3" | "1-3" | "3-3";

interface BaseSliderConnectorProps {
  type: SliderType;
  disabled?: boolean;
  disableSecondSlider?: boolean;
}

interface Slider1Props {
  title1: string;
  value1: number;
  onChange1: (value: number) => void;
  min1?: number;
  max1?: number;
}

interface Slider2Props {
  title2: string;
  value2: number;
  onChange2: (value: number) => void;
  min2?: number;
  max2?: number;
  unit2?: string;
}

interface Slider3Props {
  title3: string;
  options3: string[];
  selectedIndex3: number;
  onChange3: (index: number) => void;
}

interface Slider3SecondProps {
  title3Second: string;
  options3Second: string[];
  selectedIndex3Second: number;
  onChange3Second: (index: number) => void;
}

interface ThresholdProps {
  thresholds?: number[];
}

interface TooltipProps {
  tooltipTitle1?: string;
  tooltipContent1?: string;
  tooltipTitle2?: string;
  tooltipContent2?: string;
  tooltipTitle3?: string;
  tooltipContent3?: string;
  tooltipTitle3Second?: string;
  tooltipContent3Second?: string;
}

type SliderConnectorProps = BaseSliderConnectorProps &
  TooltipProps &
  (
    | (Slider1Props & Slider2Props & ThresholdProps)
    | (Slider2Props & Slider3Props & ThresholdProps)
    | (Slider1Props & Slider3Props)
    | (Slider3Props & Slider3SecondProps)
  );

const SliderConnector: React.FC<SliderConnectorProps> = (props) => {
  const { type, disabled = false, disableSecondSlider = false } = props;

  const [topSliderCenterY, setTopSliderCenterY] = useState(0);
  const [bottomSliderCenterY, setBottomSliderCenterY] = useState(0);
  const [, forceUpdate] = useState({});
  const containerRef = useRef<HTMLDivElement>(null);

  const getIndexFromValue = useCallback(
    (value: number, thresholds: number[]): number => {
      for (let i = 0; i < thresholds.length; i++) {
        if (value <= thresholds[i]) {
          return i;
        }
      }
      return thresholds.length;
    },
    []
  );

  const getValueFromIndex = useCallback(
    (index: number, thresholds: number[], min: number, max: number): number => {
      if (index === 0) {
        return min;
      }
      if (index >= thresholds.length) {
        return max;
      }
      return Math.round((thresholds[index - 1] + thresholds[index]) / 2);
    },
    []
  );

  useEffect(() => {
    if (type === "2-3" && "thresholds" in props && props.thresholds) {
      const slider2Props = props as Slider2Props & ThresholdProps;
      const slider3Props = props as Slider3Props;
      const newIndex = getIndexFromValue(slider2Props.value2, props.thresholds);
      if (newIndex !== slider3Props.selectedIndex3) {
        slider3Props.onChange3(newIndex);
      }
    }
  }, [type, (props as any).value2, getIndexFromValue]);

  useEffect(() => {
    if (type === "2-3" && "thresholds" in props && props.thresholds) {
      const slider2Props = props as Slider2Props;
      const slider3Props = props as Slider3Props & ThresholdProps;
      const newValue = getValueFromIndex(
        slider3Props.selectedIndex3,
        props.thresholds,
        slider2Props.min2 || 0,
        slider2Props.max2 || 100
      );
      if (Math.abs(newValue - slider2Props.value2) > 1) {
        slider2Props.onChange2(newValue);
      }
    }
  }, [type, (props as any).selectedIndex3, getValueFromIndex]);

  useEffect(() => {
    if (type === "3-3") {
      const slider3Props = props as Slider3Props;
      const slider3SecondProps = props as Slider3SecondProps;

      if (
        slider3Props.selectedIndex3 !== slider3SecondProps.selectedIndex3Second
      ) {
        slider3SecondProps.onChange3Second(slider3Props.selectedIndex3);
      }
    }
  }, [type, (props as any).selectedIndex3]);

  useEffect(() => {
    if (type === "3-3") {
      const slider3Props = props as Slider3Props;
      const slider3SecondProps = props as Slider3SecondProps;

      if (
        slider3SecondProps.selectedIndex3Second !== slider3Props.selectedIndex3
      ) {
        slider3Props.onChange3(slider3SecondProps.selectedIndex3Second);
      }
    }
  }, [type, (props as any).selectedIndex3Second]);

  // Force update connector position after render and on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      forceUpdate({});
    }, 50);
    return () => clearTimeout(timer);
  }, [topSliderCenterY, bottomSliderCenterY]);

  // Initial update on mount and observe for changes
  useEffect(() => {
    const timers = [
      setTimeout(() => forceUpdate({}), 100),
      setTimeout(() => forceUpdate({}), 200),
      setTimeout(() => forceUpdate({}), 500)
    ];
    
    // Also observe for any layout changes
    let resizeObserver: ResizeObserver | null = null;
    if (containerRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        forceUpdate({});
      });
      resizeObserver.observe(containerRef.current);
    }
    
    return () => {
      timers.forEach(clearTimeout);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  const getConnectorStyle = () => {
    if (!containerRef.current || !topSliderCenterY || !bottomSliderCenterY) {
      return { display: "none" };
    }

    // Calculate positions relative to the container
    const containerRect = containerRef.current.getBoundingClientRect();
    
    // Get the actual slider track positions
    const topSliderElement = containerRef.current.querySelector('.slider-connector-top-slider .slider3-track-container');
    const bottomSliderElement = containerRef.current.querySelector('.slider-connector-bottom-slider .slider3-track-container');
    
    if (!topSliderElement || !bottomSliderElement) {
      return { display: "none" };
    }
    
    const topRect = topSliderElement.getBoundingClientRect();
    const bottomRect = bottomSliderElement.getBoundingClientRect();
    
    // Calculate relative positions from container top
    const relativeY1 = topRect.top + topRect.height / 2 - containerRect.top;
    const relativeY2 = bottomRect.top + bottomRect.height / 2 - containerRect.top;

    const top = Math.min(relativeY1, relativeY2);
    const height = Math.abs(relativeY2 - relativeY1);

    return {
      position: "absolute" as const,
      left: "-28px", // Adjust this value to properly align with sliders
      top: `${top - 1}px`,
      height: `${height + 3}px`,
      width: "20px",
      zIndex: 10,
    };
  };

  const renderTopSlider = () => {
    if (type === "1-2" || type === "1-3") {
      const slider1Props = props as Slider1Props;
      return (
        <Slider
          title={slider1Props.title1}
          value={slider1Props.value1}
          onChange={slider1Props.onChange1}
          onPositionChange={setTopSliderCenterY}
          min={slider1Props.min1}
          max={slider1Props.max1}
          disabled={disabled}
          tooltip={
            props.tooltipTitle1 && props.tooltipContent1 ? (
              <Tooltip
                title={props.tooltipTitle1}
                content={props.tooltipContent1}
                offsetY={-7}
              />
            ) : undefined
          }
        />
      );
    } else if (type === "2-3") {
      const slider2Props = props as Slider2Props;
      return (
        <Slider2
          title={slider2Props.title2}
          value={slider2Props.value2}
          onChange={slider2Props.onChange2}
          onPositionChange={setTopSliderCenterY}
          min={slider2Props.min2}
          max={slider2Props.max2}
          unit={slider2Props.unit2}
          disabled={disabled}
          tooltip={
            props.tooltipTitle2 && props.tooltipContent2 ? (
              <Tooltip
                title={props.tooltipTitle2}
                content={props.tooltipContent2}
                offsetY={-7}
              />
            ) : undefined
          }
        />
      );
    } else if (type === "3-3") {
      const slider3Props = props as Slider3Props;
      return (
        <Slider3
          title={slider3Props.title3}
          options={slider3Props.options3}
          selectedIndex={slider3Props.selectedIndex3}
          onChange={slider3Props.onChange3}
          onPositionChange={setTopSliderCenterY}
          disabled={disabled}
          tooltip={
            props.tooltipTitle3 && props.tooltipContent3 ? (
              <Tooltip
                title={props.tooltipTitle3}
                content={props.tooltipContent3}
                offsetY={-7}
              />
            ) : undefined
          }
        />
      );
    }
  };

  const renderBottomSlider = () => {
    if (type === "1-2") {
      const slider2Props = props as Slider2Props;
      return (
        <Slider2
          title={slider2Props.title2}
          value={slider2Props.value2}
          onChange={slider2Props.onChange2}
          onPositionChange={setBottomSliderCenterY}
          min={slider2Props.min2}
          max={slider2Props.max2}
          unit={slider2Props.unit2}
          disabled={disabled || disableSecondSlider}
          tooltip={
            props.tooltipTitle2 && props.tooltipContent2 ? (
              <Tooltip
                title={props.tooltipTitle2}
                content={props.tooltipContent2}
                offsetY={-7}
              />
            ) : undefined
          }
        />
      );
    } else if (type === "1-3" || type === "2-3") {
      const slider3Props = props as Slider3Props;
      return (
        <Slider3
          title={slider3Props.title3}
          options={slider3Props.options3}
          selectedIndex={slider3Props.selectedIndex3}
          onChange={slider3Props.onChange3}
          onPositionChange={setBottomSliderCenterY}
          disabled={disabled || disableSecondSlider}
          tooltip={
            props.tooltipTitle3 && props.tooltipContent3 ? (
              <Tooltip
                title={props.tooltipTitle3}
                content={props.tooltipContent3}
                offsetY={-7}
              />
            ) : undefined
          }
        />
      );
    } else if (type === "3-3") {
      const slider3SecondProps = props as Slider3SecondProps;
      return (
        <Slider3
          title={slider3SecondProps.title3Second}
          options={slider3SecondProps.options3Second}
          selectedIndex={slider3SecondProps.selectedIndex3Second}
          onChange={slider3SecondProps.onChange3Second}
          onPositionChange={setBottomSliderCenterY}
          disabled={disabled || disableSecondSlider}
          tooltip={
            props.tooltipTitle3Second && props.tooltipContent3Second ? (
              <Tooltip
                title={props.tooltipTitle3Second}
                content={props.tooltipContent3Second}
                offsetY={-7}
              />
            ) : undefined
          }
        />
      );
    }
  };

  return (
    <div
      ref={containerRef}
      className={`slider-connector-container ${
        disabled ? "slider-connector-disabled" : ""
      }`}
      style={{ position: "relative" }}
    >
      <div className="slider-connector-top-slider">{renderTopSlider()}</div>

      <div className="slider-connector-connected-section">
        <div className="slider-connector-bottom-slider">
          {renderBottomSlider()}
        </div>
      </div>

      <div className="slider-connector-bracket" style={getConnectorStyle()}>
        <div className="slider-connector-curve-top"></div>
        <div className="slider-connector-curve-bottom"></div>
        <div className="slider-connector-lock">
          <Lock size={12} strokeWidth={2.5} />
        </div>
      </div>
    </div>
  );
};

export default SliderConnector;
